import { Component, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { lastValueFrom, scan, Subscription, takeWhile, timer } from 'rxjs';
import { ButtonComponent } from 'src/components/button/button.component';
import { IconModule } from 'src/components/icon/icon.module';
import { Form, FormError } from 'src/definitions/form.definition';
import { ForgotPinValidateOtpForm } from 'src/definitions/auth.definition';
import { FormService } from 'src/services/form.service';
import { GlobalService } from 'src/services/global.service';
import { RestService } from 'src/services/rest.service';
import { InputOtpModule } from 'primeng/inputotp';
import { AutoFocusModule } from 'primeng/autofocus';
import { CommonModule } from '@angular/common';
import { ToastService } from 'src/services/toast.service';

@Component({
  selector: 'step-otp',
  templateUrl: './step-otp.component.html',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    InputOtpModule,
    AutoFocusModule,
    IconModule,
    ButtonComponent,
  ],
})
export class _StepOtpComponent implements OnInit, OnDestroy {
  email = input<string>('');

  form: Form<ForgotPinValidateOtpForm> = new FormBuilder().nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    otp: ['', [Validators.required]],
  });

  formError: FormError<ForgotPinValidateOtpForm> = {
    label: '',
    msg: '',
    email: { label: 'E-mail', msg: '' },
    otp: { label: 'Kode OTP', msg: '' },
  };

  formSubmitting = signal<boolean>(false);

  countdown: Countdown = {
    day: 0,
    hour: 0,
    minute: 0,
    second: RESEND_OTP_SECOND,
    countdown$: undefined,
  };

  countdownSecond = signal<number>(RESEND_OTP_SECOND);

  toPrev = output<void>();
  toNext = output<string>();

  OTP_LENGTH = OTP_LENGTH;

  constructor(
    private restService: RestService,
    private toastService: ToastService,
    protected formService: FormService,
    protected globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.form.controls.email.setValue(this.email());
    this.__restartCountdown();
  }

  ngOnDestroy(): void {
    this.__stopCountdown();
  }

  protected onOtpChange() {
    this.formService.resetError(this.formError, 'otp');
    if (this.form.value.otp?.length === OTP_LENGTH) this._onSubmit();
  }

  protected onOtpPasted(event: ClipboardEvent) {
    const value = event.clipboardData?.getData('text');
    if (!Number(value)) return;
    this.form.patchValue({ otp: value?.slice(0, 6) });
  }

  protected async _onReRequestOtp() {
    if (this.formSubmitting()) return;

    try {
      this.formSubmitting.set(true);

      const payload = { email: this.email() };
      await lastValueFrom(this.restService.forgotPinRequestOtp(payload));

      this.__restartCountdown();
      this.toastService.showToast({
        summary: 'OTP berhasil dikirim',
        detail: 'Mohon cek email anda',
        severity: 'success',
      });
    } catch (error) {
      error = this.formService.serverErrorHandler(error, this.formError);
      this.globalService.errorHandler(error, 'Gagal meminta otp');
    } finally {
      this.formSubmitting.set(false);
    }
  }

  protected async _onSubmit() {
    if (this.formSubmitting()) return;

    const invalid = this.formService.validateAndGetErrorMessage(this.form, this.formError);
    if (invalid) {
      this.globalService.errorHandler(new Error(invalid), 'Gagal meminta kode OTP');
      return;
    }

    try {
      this.formSubmitting.set(true);

      const payload = { ...this.form.getRawValue() };
      const response = await lastValueFrom(this.restService.forgotPinValidateOtp(payload));

      this.toastService.showToast({
        summary: 'Berhasil',
        detail: 'Kode OTP berhasil dikirimkan ke email kamu.',
        severity: 'success',
      });

      this.toNext.emit(response.data.reset_pin_token);
    } catch (error) {
      error = this.formService.serverErrorHandler(error, this.formError);
      this.globalService.errorHandler(error, 'Gagal meminta kode OTP');
    } finally {
      this.formSubmitting.set(false);
    }
  }

  private __restartCountdown() {
    this.__stopCountdown();
    this.__startCountdown();
  }

  private __startCountdown() {
    this.countdown.countdown$ = timer(0, 1000)
      .pipe(
        scan((second) => --second, RESEND_OTP_SECOND),
        takeWhile((second) => {
          if (second <= 0) {
            this.__stopCountdown();
            return false;
          }
          return true;
        }),
      )
      .subscribe((second) => {
        this.countdown = {
          ...this.countdown,
          day: 0,
          hour: 0,
          minute: 0,
          second: second,
        };
        this.countdownSecond.set(second);
      });
  }

  private __stopCountdown() {
    this.countdown.countdown$?.unsubscribe();
    this.countdown.countdown$ = undefined;
    this.countdown = {
      day: 0,
      hour: 0,
      minute: 0,
      second: RESEND_OTP_SECOND,
    };
    this.countdownSecond.set(RESEND_OTP_SECOND);
  }
}

type Countdown = {
  day: number;
  hour: number;
  minute: number;
  second: number;
  countdown$?: Subscription;
};

const RESEND_OTP_SECOND = 60;
const OTP_LENGTH = 4;
